import { JiraDataFilterRow, JiraDataMapping, JiraMappingFieldCatalogItem, JiraOptionValue } from 'models'

/* The mapping slot that takes statuses; every other slot is a severity level. */
export const CLOSED_STATUS_SLOT = 'closed_status'

/* A filter row being edited; `uid` keeps React keys stable across reorders. */
export interface FilterDraftRow extends JiraDataFilterRow {
  uid: number
}

let rowUid = 0

export const emptyFilterRow = (): FilterDraftRow => ({ uid: ++rowUid, field: '', operator: '', values: [] })

export const toDraftRows = (filters: JiraDataFilterRow[]): FilterDraftRow[] =>
  filters.map((filter) => ({ ...filter, uid: ++rowUid }))

/* The step payload, also what the preview runs against. */
export const filtersPayload = (rows: FilterDraftRow[]): JiraDataFilterRow[] =>
  rows.map(({ field, operator, values }) => ({
    field,
    operator,
    values: values.map(({ id, name }) => ({ id, name }))
  }))

/* Zero rows is valid — every present row needs a field, an operator, and values. */
export const hasIncompleteRow = (rows: FilterDraftRow[]) =>
  rows.some((row) => !row.field || !row.operator || !row.values.length)

/* All five slots, always present. */
export const mappingPayload = (mapping: JiraDataMapping, mappingFields: JiraMappingFieldCatalogItem[]) =>
  Object.fromEntries(
    mappingFields.map((field) => [
      field.onyxia_field,
      (mapping[field.onyxia_field] ?? []).map((value) => ({
        id: value.id,
        name: value.name,
        ...(value.is_default ? { is_default: true } : {})
      }))
    ])
  )

/* Mirrors the server rule: at least one closed status and one mapped priority.
 * The API rejects an empty severity map, so this cannot be relaxed when Jira
 * returns no priorities — the step is genuinely unfinishable until the Jira
 * instance defines some. */
export const isMappingIncomplete = (mapping: JiraDataMapping, mappingFields: JiraMappingFieldCatalogItem[]) => {
  const severities = mappingFields.filter((field) => field.onyxia_field !== CLOSED_STATUS_SLOT)
  const mapped = severities.reduce((total, field) => total + (mapping[field.onyxia_field]?.length ?? 0), 0)

  return !(mapping[CLOSED_STATUS_SLOT]?.length ?? 0) || !mapped
}

/* Option values are matched by name (email for users); the id is display and
 * rehydration metadata, so the select works on names. */
export const toOption = (value: JiraOptionValue) => ({ label: value.display_name || value.name, value: value.name })

/* Map selected option names back to source values, keeping their ids and
 * tolerating values that have since disappeared from Jira. */
export const resolveOptionValues = (
  names: string[],
  available: JiraOptionValue[],
  current: JiraOptionValue[] = []
): JiraOptionValue[] =>
  names.map(
    (name) =>
      available.find((value) => value.name === name) ??
      current.find((value) => value.name === name) ?? { id: null, name }
  )

export const isDoneCategory = (value: JiraOptionValue) => (value.status_category ?? '').toLowerCase() === 'done'
